import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { useMe } from '../auth/context';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import type { DashboardSupport, TeamOverview } from '../api/types';
import { isBackOffice } from '../layout/navigation';
import { GoalStat } from '../ops/GoalStat';
import { compareLabel } from '../ops/trend';
import { NextCallCard } from '../ops/NextCallCard';
import { MaklerTable, TelesalesTable } from '../ops/TeamTables';
import { TodayFollowUps } from '../ops/TodayFollowUps';
import { useFieldOnly, useProject, useSupportOnly, useTelesalesOnly } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { FieldSalesPage } from './FieldSalesPage';
import { TicketList } from '../ops/TicketCards';
import { MaklerFollowUps, MaklerUpcoming } from '../ops/MaklerOverview';
import { DueReports } from '../ops/report/DueReports';

function Stat({ value, label, to, tone }: { value: number; label: string; to?: string; tone?: 'warn' | 'error' }) {
  const body = (
    <>
      <span className={tone && value > 0 ? `stat-value stat-${tone}` : 'stat-value'}>{value}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  return <li className="stat">{to ? <Link to={to}>{body}</Link> : body}</li>;
}

// Startseite „Heute“ nach dem Grundsatz „Das ist jetzt deine nächste Aufgabe“ – je Rolle: Makler sehen ihren
// Tag mit Route; Telefonisten den nächsten Anruf (mit Grund) und die Wiedervorlagen von heute; der Support die
// nächste Aufgabe aus dem Posteingang und seine Wiedervorlagen; die Leitung zusätzlich das Team.
export function HomePage() {
  return useFieldOnly() ? <MaklerHome /> : <OfficeHome />;
}

// Makler (OFFENE_FRAGEN B-28): heute mit Route, dann was als Nächstes kommt – eigene Termine, eigene Rückrufe und die
// Rückfragen, an denen er beteiligt ist (nicht alle des Makler-Teams).
function MaklerHome() {
  const { project } = useProject();
  return (
    <>
      <div className="page page-top">
        <DueReports />
      </div>
      <FieldSalesPage />
      <div className="page">
        <div className="today-grid">
          <MaklerUpcoming />
          <MaklerFollowUps />
        </div>
        <section aria-labelledby="requests-title">
          <div className="section-head">
            <h2 id="requests-title">Meine offenen Rückfragen</h2>
            <Link to={`/p/${project.key}/tickets`}>Alle Rückfragen →</Link>
          </div>
          <TicketList q={{ status: ['open'], assigned: 'involved', sort: 'priority' }} />
        </section>
      </div>
    </>
  );
}

function OfficeHome() {
  const me = useMe();
  const { project, can } = useProject();
  const papi = useProjectApi();
  const fieldOnly = useFieldOnly();
  const backOffice = isBackOffice({ can, fieldOnly });
  const leadership = can('call.review') || can('project.manage');
  // Reiner Support: auf „Heute“ die nächste eigene Aufgabe statt der Akquise, ohne Wiedervorlagen der Terminplanung.
  const supportOnly = useSupportOnly();
  const telesalesOnly = useTelesalesOnly();
  const dash = useQuery({ queryKey: projectKeys.dashboard(project.id), queryFn: ({ signal }) => papi.dashboard(signal), refetchInterval: 60_000 });
  const team = useQuery({
    queryKey: projectKeys.team(project.id),
    queryFn: ({ signal }) => papi.teamOverview(signal),
    enabled: leadership,
    refetchInterval: 120_000,
  });
  const base = `/p/${project.key}`;
  const inbox = (tab: string) => `${base}/posteingang?ansicht=${tab}`;
  const t = dash.data?.telesales;
  const s = dash.data?.support;

  return (
    <div className="page">
      <h1>Guten Tag, {me.user.first_name}</h1>
      {can('call.start') && !supportOnly ? (
        <div className="today-grid">
          <NextCallCard />
          <TodayFollowUps callable allTo={`${base}/akquise?ansicht=wiedervorlagen`} />
        </div>
      ) : (
        backOffice && (
          <div className="today-grid">
            <NextInboxCard s={s ?? undefined} base={base} focused={supportOnly} />
            {can('task.read') && !supportOnly && <TodayFollowUps callable={false} allTo={inbox('wiedervorlagen')} />}
          </div>
        )
      )}
      {t && (
        <section className="card" aria-labelledby="telesales-title">
          <h2 id="telesales-title">Mein Tag</h2>
          <ul className="stats">
            <GoalStat
              value={t.calls_today}
              target={t.targets?.calls || undefined}
              label="Anrufe"
              previous={t.previous?.calls}
              previousLabel={compareLabel(t.previous?.date)}
            />
            <GoalStat
              value={t.conversations_today}
              target={t.targets?.conversations || undefined}
              label="Gespräche"
              previous={t.previous?.conversations}
              previousLabel={compareLabel(t.previous?.date)}
            />
            <GoalStat
              value={t.appointments_created_today}
              target={t.targets?.appointments || undefined}
              label="Termine vereinbart"
              previous={t.previous?.appointments_created}
              previousLabel={compareLabel(t.previous?.date)}
            />
            {!telesalesOnly && (
              <Stat value={t.pending_ai_reviews} label="KI-Vorschläge zu prüfen" to={can('ai.review') ? (backOffice ? inbox('ki') : `${base}/ki-pruefung`) : undefined} />
            )}
          </ul>
          {t.previous && <p className="muted small stats-note">Vergleich: {compareLabel(t.previous.date)} bis zur gleichen Uhrzeit.</p>}
        </section>
      )}
      {!backOffice && can('ticket.read') && can('appointment.reschedule') && (
        <section className="card" aria-labelledby="my-tickets-title">
          <div className="section-head">
            <h2 id="my-tickets-title">Meine offenen Aufgaben &amp; Rückfragen</h2>
            <Link to={`${base}/tickets`}>Alle →</Link>
          </div>
          <TicketList q={{ status: ['open'], assigned: 'me', sort: 'priority' }} />
        </section>
      )}
      {s && backOffice && supportOnly && (
        <section className="card" aria-labelledby="inbox-title">
          <h2 id="inbox-title">
            <Link to={`${base}/posteingang`}>Meine Aufgaben</Link>
          </h2>
          <ul className="stats">
            <Stat value={s.open_tickets} label="offene Aufgaben" to={inbox('rueckfragen')} />
            <Stat value={s.urgent_tickets} label="davon dringend" tone="error" to={inbox('rueckfragen')} />
            <Stat value={s.field_sales_requests} label="von Maklern" to={inbox('rueckfragen')} />
            <Stat value={s.geo_review_open} label="Adresse unklar" to={can('lead.geo_review') ? inbox('adressen') : undefined} />
          </ul>
        </section>
      )}
      {s && backOffice && !supportOnly && (
        <section className="card" aria-labelledby="inbox-title">
          <h2 id="inbox-title">
            <Link to={`${base}/posteingang`}>Posteingang</Link>
          </h2>
          <ul className="stats">
            <Stat value={s.open_tickets} label="offene Rückfragen" to={inbox('rueckfragen')} />
            <Stat value={s.urgent_tickets} label="davon dringend" tone="error" to={inbox('rueckfragen')} />
            <Stat value={s.field_sales_requests} label="von Maklern" to={inbox('rueckfragen')} />
            <Stat value={s.appointment_handbacks} label="Terminrückgaben" tone="warn" to={inbox('rueckfragen')} />
            <Stat value={s.overdue_follow_ups} label="überfällige Wiedervorlagen" tone="warn" to={inbox('wiedervorlagen')} />
            <Stat value={s.unassigned_appointments} label="Termine ohne Makler" tone="warn" to={inbox('ohne-makler')} />
            <Stat value={s.geo_review_open} label="Adresse unklar" to={can('lead.geo_review') ? inbox('adressen') : undefined} />
            <Stat value={s.ai_reviews_pending} label="KI-Prüfungen" to={can('ai.review') ? inbox('ki') : undefined} />
            <Stat value={s.sync_failures} label="Outlook-Fehler" tone="error" to={can('integration.outlook.manage') ? inbox('outlook') : undefined} />
            <Stat value={s.appointments_next_7_days} label="Termine (7 Tage)" to={`${base}/termine`} />
            <Stat value={s.active_calls} label="laufende Anrufe" />
          </ul>
        </section>
      )}
      {leadership && (
        <section className="card" aria-labelledby="team-title">
          <h2 id="team-title">Team heute</h2>
          {team.isPending ? (
            <PageLoader />
          ) : team.isError ? (
            <ErrorMessage error={team.error} onRetry={() => void team.refetch()} />
          ) : (
            <>
              <h3>Telefonisten</h3>
              <TelesalesTable telesales={team.data.telesales} fieldSales={team.data.field_sales} />
              <h3>Makler</h3>
              <MaklerTable fieldSales={team.data.field_sales} />
              <p>
                <Link to={`${base}/gebiete`}>Alle Gebiete & Makler →</Link>
              </p>
            </>
          )}
        </section>
      )}
      {leadership && team.data?.inventory && <InventoryCard inv={team.data.inventory} />}
    </div>
  );
}

// InventoryCard: Bestand nach Ebenen (OFFENE_FRAGEN B-20) – Lead = Geschäftsmöglichkeit mit einem oder mehreren
// Objekten, Eigentümer = Personen, Haushalt = Personen, die zusammengehören.
function InventoryCard({ inv }: { inv: NonNullable<TeamOverview['inventory']> }) {
  const n = (v: number) => v.toLocaleString('de-DE');
  const items: [number, string][] = [
    [inv.leads, 'Leads'],
    [inv.leads_multi_object, 'davon mit mehreren Objekten'],
    [inv.objects, 'Objekte'],
    [inv.owners, 'Eigentümer'],
    [inv.households, 'Haushalte'],
    [inv.contact_units, 'Kontakteinheiten'],
    [inv.phone_numbers, 'Telefonnummern'],
    [inv.suppressed, 'auf der Sperrliste'],
  ];
  return (
    <section className="card" aria-labelledby="inventory-title">
      <h2 id="inventory-title">Bestand</h2>
      <ul className="stats">
        {items.map(([v, label]) => (
          <li key={label} className="stat">
            <span className="stat-value">{n(v)}</span>
            <span className="stat-label">{label}</span>
          </li>
        ))}
      </ul>
      <p className="muted small stats-note">
        Ein Lead ist eine Geschäftsmöglichkeit mit einem oder mehreren Objekten. Haushalte: {n(inv.household_persons)} Personen, die
        zusammengehören; Kontakteinheiten = Haushalte plus Eigentümer ohne Haushalt. Stand {new Date(inv.as_of).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr.
      </p>
    </section>
  );
}

// NextInboxCard: nächste Aufgabe des Supports – die dringendste nicht leere Liste im Posteingang.
function NextInboxCard({ s, base, focused = false }: { s?: DashboardSupport; base: string; focused?: boolean }) {
  const { can } = useProject();
  const inbox = (tab: string) => `${base}/posteingang?ansicht=${tab}`;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const steps = !s
    ? []
    : focused
      ? [
          { n: s.urgent_tickets, title: plural(s.urgent_tickets, 'dringende Aufgabe', 'dringende Aufgaben'), hint: 'Makler oder Kollegen warten auf eine Antwort.', to: inbox('rueckfragen') },
          { n: s.field_sales_requests, title: plural(s.field_sales_requests, 'Rückfrage von Maklern', 'Rückfragen von Maklern'), hint: 'Offene Fragen aus dem Außendienst.', to: inbox('rueckfragen') },
          { n: s.open_tickets, title: plural(s.open_tickets, 'offene Aufgabe', 'offene Aufgaben'), hint: 'Infos, Daten, Unterlagen und Verträge der Reihe nach erledigen.', to: inbox('rueckfragen') },
          { n: can('lead.geo_review') ? s.geo_review_open : 0, title: plural(s.geo_review_open, 'Adresse klären', 'Adressen klären'), hint: 'Ohne eindeutige Adresse kein Gebiet und kein Makler.', to: inbox('adressen') },
          {
            n: can('ai.review') ? (s.ai_contact_reviews_pending ?? 0) : 0,
            title: plural(s.ai_contact_reviews_pending ?? 0, 'Kontaktdaten prüfen', 'Kontaktdaten prüfen'),
            hint: 'Von der KI im Gespräch gehörte Kontaktdaten übernehmen oder verwerfen.',
            to: inbox('ki'),
          },
        ]
      : [
        { n: s.urgent_tickets, title: plural(s.urgent_tickets, 'dringende Rückfrage', 'dringende Rückfragen'), hint: 'Makler oder Kollegen warten auf eine Antwort.', to: inbox('rueckfragen') },
        { n: s.appointment_handbacks, title: plural(s.appointment_handbacks, 'Terminrückgabe', 'Terminrückgaben'), hint: 'Makler kann nicht – Kunden kontaktieren und neu organisieren.', to: inbox('rueckfragen') },
        { n: s.field_sales_requests, title: plural(s.field_sales_requests, 'Rückfrage von Maklern', 'Rückfragen von Maklern'), hint: 'Offene Fragen aus dem Außendienst.', to: inbox('rueckfragen') },
        { n: s.unassigned_appointments, title: plural(s.unassigned_appointments, 'Termin ohne Makler', 'Termine ohne Makler'), hint: 'Makler zuweisen, damit der Termin stattfindet.', to: inbox('ohne-makler') },
        { n: s.overdue_follow_ups, title: plural(s.overdue_follow_ups, 'überfällige Wiedervorlage', 'überfällige Wiedervorlagen'), hint: 'Zugesagte Rückmeldungen nachholen.', to: inbox('wiedervorlagen') },
        { n: can('integration.outlook.manage') ? s.sync_failures : 0, title: plural(s.sync_failures, 'Outlook-Fehler', 'Outlook-Fehler'), hint: 'Termine sind nicht im Kalender des Maklers.', to: inbox('outlook') },
        { n: can('lead.geo_review') ? s.geo_review_open : 0, title: plural(s.geo_review_open, 'Adresse klären', 'Adressen klären'), hint: 'Ohne eindeutige Adresse kein Gebiet und kein Makler.', to: inbox('adressen') },
        { n: can('ai.review') ? s.ai_reviews_pending : 0, title: plural(s.ai_reviews_pending, 'KI-Vorschlag prüfen', 'KI-Vorschläge prüfen'), hint: 'Aus Gesprächen abgeleitete Angaben bestätigen.', to: inbox('ki') },
        { n: s.open_tickets, title: plural(s.open_tickets, 'offene Rückfrage', 'offene Rückfragen'), hint: 'Rückfragen der Reihe nach abarbeiten.', to: inbox('rueckfragen') },
      ];
  const next = steps.find((step) => step.n > 0);
  return (
    <section className="card next-task" aria-labelledby="next-inbox-title">
      <p className="eyebrow">Als Nächstes</p>
      {!s ? (
        <p className="muted">Wird ermittelt …</p>
      ) : next ? (
        <>
          <h2 id="next-inbox-title">{next.title}</h2>
          <p className="muted">{next.hint}</p>
          <div className="form-actions">
            <Link className="btn btn-primary" to={next.to}>
              Öffnen
            </Link>
          </div>
        </>
      ) : (
        <>
          <h2 id="next-inbox-title">{focused ? 'Alles erledigt' : 'Posteingang leer'}</h2>
          <p className="muted">Gerade ist nichts offen – neue {focused ? 'Aufgaben' : 'Rückfragen'} erscheinen hier automatisch.</p>
        </>
      )}
      <p className="next-call-order muted">
        {focused
          ? 'Reihenfolge: dringende Aufgaben → Rückfragen von Maklern → offene Aufgaben → Adressen → Kontaktdaten (KI).'
          : 'Reihenfolge: dringende Rückfragen → Terminrückgaben → Rückfragen von Maklern → Termine ohne Makler → überfällige Wiedervorlagen → Adressen → KI-Prüfung.'}
      </p>
    </section>
  );
}
